import { defineStore } from 'pinia';
import { PaginatedResponse } from 'src/interfaces/api';
import {
  AdviceCategory,
  Allergy,
  Competency,
  FieldIdentifier,
  DuplicateGroup,
  Equipment,
  ExpiringCompetencyNotice,
  GPSurgery,
  Group,
  IncidentReport,
  ITIncident,
  CustomerComplaint,
  Permission,
  SafetyReport,
  Staff,
  StaffCompetency,
  StaffSettings,
  User,
  VaccinationHistory,
  VaccinationHistoryExtraData,
  VaccinationMappingField,
  VaccineProduct,
} from 'src/interfaces/models';
import { AxiosError, AxiosResponse } from 'axios';
import { LocationQueryValue } from 'vue-router';
import { defaultUser } from 'stores/auth';
import {
  Appointment,
  AppointmentCategory,
  AppointmentOutcome,
  AppointmentType,
  AppointmentTypeTag,
  Availability,
  EForm,
  Room,
  Site,
} from 'src/interfaces/services';
import { FormElement, SourceFieldOption } from 'src/interfaces/form-builder';
import { v4 as uuidv4 } from 'uuid';

export interface AdminStateInterface {
  groups: {
    loading: boolean;
    data: PaginatedResponse<Group>;
    modal: AdminModalOptionsInterface<Group>;
  };
  permissions: Permission[];
  users: {
    loading: boolean;
    data: PaginatedResponse<User>;
    modal: AdminModalOptionsInterface<User>;
    appointmentsModal: AdminModalOptionsInterface<User>;
  };
  gp_surgeries: {
    loading: boolean;
    data: PaginatedResponse<GPSurgery>;
    modal: AdminModalOptionsInterface<GPSurgery>;
  };
  staff: {
    loading: boolean;
    data: PaginatedResponse<Staff>;
    modal: AdminModalOptionsInterface<Staff>;
  };
  competencies: {
    loading: boolean;
    data: PaginatedResponse<Competency>;
    modal: AdminModalOptionsInterface<Competency>;
  };
  staff_competencies: {
    loading: boolean;
    data: PaginatedResponse<StaffCompetency>;
    modal: AdminModalOptionsInterface<StaffCompetency>;
    expiring_dialog: {
      show: boolean;
      data: ExpiringCompetencyNotice[];
    };
  };
  sites: {
    loading: boolean;
    data: PaginatedResponse<Site>;
    modal: AdminModalOptionsInterface<Site>;
  };
  rooms: {
    loading: boolean;
    data: PaginatedResponse<Room>;
    modal: AdminModalOptionsInterface<Room>;
  };
  eforms: {
    loading: boolean;
    data: PaginatedResponse<EForm>;
    modal: AdminModalOptionsInterface<EForm>;
    fields: SourceFieldOption[];
  };
  appointments: {
    loading: boolean;
    deleteLoading: boolean;
    data: PaginatedResponse<Appointment>;
    modal: AdminModalOptionsInterface<Appointment>;
  };
  cancelledAppointments: {
    loading: boolean;
    data: PaginatedResponse<Appointment>;
  };
  patients: User[];
  appointment_types: {
    loading: boolean;
    data: PaginatedResponse<AppointmentType>;
    modal: AdminModalOptionsInterface<AppointmentType>;
  };
  appointment_type_tags: {
    loading: boolean;
    data: PaginatedResponse<AppointmentTypeTag>;
    modal: AdminModalOptionsInterface<AppointmentTypeTag>;
  };
  appointment_categories: {
    loading: boolean;
    data: PaginatedResponse<AppointmentCategory>;
    modal: AdminModalOptionsInterface<AppointmentCategory>;
  };
  appointment_outcomes: {
    loading: boolean;
    data: PaginatedResponse<AppointmentOutcome>;
    modal: AdminModalOptionsInterface<AppointmentOutcome>;
  };
  vaccine_products: {
    loading: boolean;
    data: PaginatedResponse<VaccineProduct>;
    modal: AdminModalOptionsInterface<VaccineProduct>;
  };
  vaccination_histories: {
    loading: boolean;
    data: PaginatedResponse<VaccinationHistory>;
    modal: AdminModalOptionsInterface<VaccinationHistory>;
    extraData: VaccinationHistoryExtraData | Record<string, never>;
    fields: VaccinationMappingField[];
    appointmentModal: {
      show: boolean;
      loading: boolean;
      item: VaccinationHistory;
      items: VaccinationHistory[];
      extraData: VaccinationHistoryExtraData | Record<string, never>;
    };
    administration_choices: {
      route_of_administration: object[];
      site_of_administration: object[];
    };
  };
  advice_categories: {
    loading: boolean;
    data: PaginatedResponse<AdviceCategory>;
    modal: AdminModalOptionsInterface<AdviceCategory>;
  };
  field_identifiers: {
    loading: boolean;
    data: PaginatedResponse<FieldIdentifier>;
    modal: AdminModalOptionsInterface<FieldIdentifier>;
    choices: {
      source: Array<{ label: string; value: string }>;
      calculation: Array<{ label: string; value: string }>;
    };
  };
  allergies: {
    loading: boolean;
    data: PaginatedResponse<Allergy>;
    modal: AdminModalOptionsInterface<Allergy>;
  };
  duplicatePatients: {
    modal: {
      show: boolean;
      data: User[];
    };
    duplicateGroups: {
      loading: boolean;
      data: PaginatedResponse<DuplicateGroup>;
      modal: AdminModalOptionsInterface<DuplicateGroup>;
    };
  };
  equipment: {
    loading: boolean;
    data: PaginatedResponse<Equipment>;
    modal: AdminModalOptionsInterface<Equipment>;
  };
  incident_reports: {
    loading: boolean;
    data: PaginatedResponse<IncidentReport>;
    modal: AdminModalOptionsInterface<IncidentReport>;
    viewModal: AdminModalOptionsInterface<IncidentReport>;
  };
  it_incidents: {
    loading: boolean;
    data: PaginatedResponse<ITIncident>;
    modal: AdminModalOptionsInterface<ITIncident>;
    viewModal: AdminModalOptionsInterface<ITIncident>;
  };
  customer_complaints: {
    loading: boolean;
    data: PaginatedResponse<CustomerComplaint>;
    modal: AdminModalOptionsInterface<CustomerComplaint>;
    viewModal: AdminModalOptionsInterface<CustomerComplaint>;
  };
  staff_settings: {
    loading: boolean;
    data: PaginatedResponse<StaffSettings>;
    modal: AdminModalOptionsInterface<StaffSettings>;
  };
  monthly_reports: {
    loading: boolean;
    data: PaginatedResponse<SafetyReport>;
  };
  annual_reports: {
    loading: boolean;
    data: PaginatedResponse<SafetyReport>;
  };
}

export interface AdminModalOptionsInterface<T> {
  show: boolean;
  loading: boolean;
  item: T;
}

export const defaultGroup = (): Group => {
  return {
    name: '',
    url: '',
    permissions: [],
    selected_permissions: [],
  };
};

export const defaultStaff = (): Staff => {
  return {
    id: null,
    url: '',
    user_id: null,
    pharmacist_reg_number: null,
    competencies: [],
    is_active: true,
  };
};

export const defaultSite = (): Site => {
  return {
    id: null,
    url: '',
    name: '',
    hidden: false,
    slug: '',
    address1: null,
    address2: null,
    city: null,
    county: null,
    nation: null,
    postcode: null,
    telephone: null,
    email: null,
  };
};

export const defaultGPSurgery = (): GPSurgery => {
  return {
    id: null,
    url: '',
    name: '',
    address1: null,
    address2: null,
    city: null,
    county: null,
    nation: null,
    postcode: null,
    telephone: null,
    email: null,
  };
};

export const defaultRoom = (): Room => {
  return {
    id: null,
    url: '',
    name: '',
    site_id: null,
    slug: '',
    equipment_ids: [],
  };
};

export const defaultCompetency = (): Competency => {
  return {
    url: '',
    name: '',
  };
};

export const defaultStaffCompetency = (): StaffCompetency => {
  return {
    url: '',
    id: null,
    staff_id: null,
    competency_id: null,
    expires_at: '',
    issued_at: '',
  };
};

export const defaultEForm = (): EForm => {
  return {
    url: '',
    name: '',
    title: '',
    description: '',
    version: '',
    subversion: '',
    formDefinition: [],
    is_vaccination_form: false,
  };
};

export const defaultAppointment = (): Appointment => {
  return {
    id: 0,
    url: '',
    patient: {} as User,
    patient_id: null,
    appointment_type: defaultAppointmentType(),
    appointment_type_id: null,
    appointment_type_url: '',
    availability: {} as Availability,
    availability_id: null,
    completed_by: null,
    gp_surgery: null,
    outcomes: [],
    start: '',
    duration: 0,
    created: '',
    modified: '',
    completed: false,
    completed_on: null,
    patient_address: '',
    room: {} as Room,
  };
};

export const defaultAppointmentType = (): AppointmentType => {
  return {
    url: '',
    id: null,
    name: '',
    description: '',
    disabled: false,
    hidden: false,
    slug: '',
    duration: 0,
    prebooking_info: '',
    competencies: [],
    categories: [],
    patient_forms: [],
    clinician_forms: [],
    sequential_forms: false,
    outcomes: [],
    tag_ids: [],
    equipment_ids: [],
  };
};

export const defaultAppointmentTypeTag = (): AppointmentTypeTag => {
  return {
    url: '',
    id: null,
    name: '',
    slug: '',
  };
};

export const defaultAppointmentCategory = (): AppointmentCategory => {
  return {
    url: '',
    id: null,
    name: '',
    disabled: false,
    hidden: false,
    level: 0,
    lft: 0,
    parent: '',
    rght: 0,
    tree_id: null,
  };
};

export const defaultAppointmentOutcome = (): AppointmentOutcome => {
  return {
    url: '',
    id: null,
    name: '',
    archived: false,
    keep_version: false,
    subversion: 0,
    template_code: '',
    template_path: null,
    version: 0,
    certificate_mode: '',
    attach_identifier: '',
    attach_value: '',
    collect_supplied_items: false,
    vaccine_products: [],
  };
};

export const defaultVaccineProduct = (): VaccineProduct => {
  return {
    id: null,
    url: '',
    name: '',
    description: null,
    doses_required: null,
    booster_requirement_years: null,
    dose_schedules: [],
    is_active: true,
    pgd_certified: true,
    price: null,
    created: '',
    modified: '',
  };
};

export const defaultVaccinationHistory = (): VaccinationHistory => {
  return {
    id: null,
    url: '',
    patient_id: null,
    staff_id: null,
    appointment_id: null,
    vaccine_product_id: null,
    batch_number: '',
    expiry_date: null,
    dose_number: null,
    date_administered: new Date().toISOString(),
    route_of_administration: null,
    site_of_administration: null,
    was_administered: true,
    consent_obtained: false,
    booster_due_date: null,
    adverse_reactions: null,
    is_historical: false,
    external_clinic_name: null,
    external_clinic_address: null,
    administered_by: null,
    notes: null,
    price: null,
    created: '',
    modified: '',
  };
};

export const defaultAdviceCategory = (): AdviceCategory => {
  return {
    id: null,
    url: '',
    name: '',
    created: '',
    modified: '',
  };
};

export const defaultFieldIdentifier = (): FieldIdentifier => {
  return {
    id: null,
    url: '',
    name: '',
    key: '',
    description: '',
    source: null,
    calculation: null,
    created: '',
    modified: '',
  };
};

export const defaultAllergy = (): Allergy => {
  return {
    id: null,
    url: '',
    name: '',
    description: null,
  };
};

export const defaultEquipment = (): Equipment => {
  return {
    url: '',
    name: '',
  };
};

export const defaultIncidentReport = (): IncidentReport => {
  return {
    id: null,
    uuid: '',
    url: '',
    incident_datetime: '',
    report_datetime: '',
    reporter_id: null,
    reporter_details: {},
    is_staff_involved: false,
    other_staff_involved: [],
    is_patient_involved: true,
    patient_id: null,
    patient_details: {},
    incident_type: '',
    incident_subtype: null,
    is_lasa: false,
    incident_description: '',
    incident_location_id: null,
    are_medications_involved: false,
    medications_involved: [],
    patient_took_incorrect_medication: false,
    physical_harm_level: 'NO_HARM',
    psychological_harm_level: 'NO_HARM',
    potential_harm_level: 'NO_HARM',
    patient_outcome: '',
    contributing_factors: [],
    contributing_factors_other: '',
    preventive_actions_taken: '',
    actions_to_prevent_recurrence: '',
    recurrence_action_status: '',
    patient_follow_up: '',
    note_on_patient_record: false,
    patient_record_notes: '',
    lfpse_reference_number: null,
    created: '',
    modified: '',
  };
};

export const defaultITIncident = (): ITIncident => {
  return {
    id: null,
    uuid: '',
    url: '',
    report_datetime: '',
    report_date: '',
    report_time: '',
    reporter_id: null,
    reporter_details: {},
    is_staff_involved: false,
    other_staff_involved: [],
    system_affected: '',
    scope: '',
    outage_start: '',
    outage_start_date: '',
    outage_start_time: '',
    outage_end: '',
    outage_end_date: '',
    outage_end_time: '',
    how_work_continued: '',
    reported_to: '',
    location_id: null,
    description: '',
    resolution_status: 'OPEN',
    resolution_notes: '',
    created: '',
    modified: '',
  };
};

export const defaultCustomerComplaint = (): CustomerComplaint => {
  return {
    id: null,
    uuid: '',
    url: '',
    report_datetime: '',
    report_date: '',
    report_time: '',
    reporter_id: null,
    reporter_details: {},
    is_staff_involved: false,
    other_staff_involved: [],
    complainant_name: '',
    date_of_complaint: '',
    location_id: null,
    nature_of_complaint: '',
    investigation: '',
    outcome: '',
    gp_informed: false,
    follow_up_required: false,
    follow_up_notes: '',
    resolution_status: 'OPEN',
    created: '',
    modified: '',
  };
};

export const defaultStaffSettings = (): StaffSettings => {
  return {
    url: '',
    id: null,
    staff_id: null,
    notify_all_bookings: false,
    notify_completion: false,
    digest_type: 'DAILY',
  };
};

export const useAdminStore = defineStore('admin', {
  state: (): AdminStateInterface => ({
    groups: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultGroup(),
      },
    },
    permissions: [],
    users: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultUser(),
      },
      appointmentsModal: {
        show: false,
        loading: false,
        item: defaultUser(),
      },
    },
    gp_surgeries: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultGPSurgery(),
      },
    },
    staff: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultStaff(),
      },
    },
    competencies: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultCompetency(),
      },
    },
    staff_competencies: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultStaffCompetency(),
      },
      expiring_dialog: {
        show: false,
        data: [],
      },
    },
    sites: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultSite(),
      },
    },
    rooms: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultRoom(),
      },
    },
    eforms: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultEForm(),
      },
      fields: [],
    },
    appointments: {
      loading: false,
      deleteLoading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultAppointment(),
      },
    },
    cancelledAppointments: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
    },
    patients: [],
    appointment_types: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultAppointmentType(),
      },
    },
    appointment_type_tags: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultAppointmentTypeTag(),
      },
    },
    appointment_categories: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultAppointmentCategory(),
      },
    },
    appointment_outcomes: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultAppointmentOutcome(),
      },
    },
    vaccine_products: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultVaccineProduct(),
      },
    },
    vaccination_histories: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultVaccinationHistory(),
      },
      extraData: {},
      fields: {} as VaccinationMappingField[],
      appointmentModal: {
        show: false,
        loading: false,
        item: defaultVaccinationHistory(),
        items: [],
        extraData: {},
      },
      administration_choices: {
        route_of_administration: [],
        site_of_administration: [],
      },
    },
    advice_categories: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultAdviceCategory(),
      },
    },
    field_identifiers: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultFieldIdentifier(),
      },
      choices: {
        source: [],
        calculation: [],
      },
    },
    allergies: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultAllergy(),
      },
    },
    duplicatePatients: {
      modal: {
        show: false,
        data: [],
      },
      duplicateGroups: {
        loading: false,
        data: {
          count: 0,
          results: [],
        },
        modal: {
          show: false,
          loading: false,
          item: {} as DuplicateGroup,
        },
      },
    },
    equipment: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultEquipment(),
      },
    },
    incident_reports: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultIncidentReport(),
      },
      viewModal: {
        show: false,
        loading: false,
        item: {} as IncidentReport,
      },
    },
    it_incidents: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultITIncident(),
      },
      viewModal: {
        show: false,
        loading: false,
        item: {} as ITIncident,
      },
    },
    customer_complaints: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultCustomerComplaint(),
      },
      viewModal: {
        show: false,
        loading: false,
        item: {} as CustomerComplaint,
      },
    },
    staff_settings: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
      modal: {
        show: false,
        loading: false,
        item: defaultStaffSettings(),
      },
    },
    monthly_reports: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
    },
    annual_reports: {
      loading: false,
      data: {
        count: 0,
        results: [],
      },
    },
  }),

  actions: {
    getGroupData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.groups.loading = true;

        await this.api
          .get<PaginatedResponse<Group> | Group[]>('admin/groups/', {
            params: { ...payload },
          })
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.groups.data = response.data;
            } else {
              this.groups.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.groups.loading = false;
      });
    },

    getGroupItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.groups.modal.loading = true;

        await this.api
          .get<Group>(url)
          .then((response: AxiosResponse) => {
            this.groups.modal.item = response.data;
            this.groups.modal.item.selected_permissions =
              this.permissions.filter((item: Permission) =>
                response.data.permissions.includes(item.url)
              );

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.groups.modal.loading = false;
      });
    },

    createGroupItem(payload: Group): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.groups.modal.loading = true;

        await this.api
          .post<Group>('admin/groups/', {
            name: payload.name,
            permissions: payload.permissions,
          })
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.groups.modal.loading = false;
      });
    },

    updateGroupItem(payload: Group): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.groups.modal.loading = true;

        await this.api
          .put<Group>(payload.url, {
            name: payload.name,
            permissions: payload.permissions,
          })
          .then((response: AxiosResponse) => {
            if (response?.status !== 400) {
              const index = this.groups.data.results.findIndex(
                (item: Group) => item.url === response.data.url
              );
              if (index !== -1) {
                this.groups.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.groups.modal.loading = false;
      });
    },

    deleteGroupItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showGroupModal(url?: string | null) {
      this.groups.loading = true;
      if (url) {
        await this.getGroupItem(url);
      }
      this.groups.modal.show = true;
      this.groups.loading = false;
    },

    hideGroupModal() {
      this.groups.modal.show = false;
      this.groups.modal.item = defaultGroup();
    },

    getPermissionData(): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get<Permission>('admin/permissions/')
          .then((response: AxiosResponse) => {
            this.permissions = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    getUserData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.users.loading = true;

        await this.api
          .get<PaginatedResponse<User> | User[]>('admin/users/', {
            params: { ...payload },
          })
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.users.data = response.data;
            } else {
              this.users.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.users.loading = false;
      });
    },

    getUserItem(url: string, payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get<User>(url, { params: { ...payload } })
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    createUserItem(payload: User): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.users.modal.loading = true;

        await this.api
          .post<User>('admin/users/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.users.modal.loading = false;
      });
    },

    updateUserItem(payload: User): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.users.modal.loading = true;

        await this.api
          .put<User>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.users.data.results.findIndex(
                (item: User) => item.url === response.data.url
              );
              if (index !== -1) {
                this.users.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.users.modal.loading = false;
      });
    },

    deleteUserItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    acknowledgeBoosters(userId: number): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .post(`admin/users/${userId}/acknowledge-boosters/`)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showUserModal(url?: string | null, payload: object = {}) {
      this.users.loading = true;
      if (url) {
        this.users.modal.loading = true;
        await this.getUserItem(url, payload)
          .then((response: AxiosResponse) => {
            this.users.modal.item = response.data;
            this.users.modal.item.selected_groups =
              this.groups.data.results.filter((item: Group) =>
                response.data.groups.includes(item.url)
              );
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.users.modal.loading = false;
            this.users.modal.show = true;
            this.users.loading = false;
          });
      } else {
        this.users.modal.show = true;
        this.users.loading = false;
      }
    },

    hideUserModal() {
      this.users.modal.show = false;
      this.users.modal.item = defaultUser();
    },

    async showUserAppointmentsModal(url: string, payload: object = {}) {
      this.users.loading = true;
      this.users.appointmentsModal.show = true;
      this.users.appointmentsModal.loading = true;

      await this.getUserItem(url, payload)
        .then((response: AxiosResponse) => {
          this.users.appointmentsModal.item = response.data;
        })
        .catch((e) => {
          console.error(e);
        });

      this.users.appointmentsModal.loading = false;
      this.users.loading = false;
    },

    hideUserAppointmentsModal() {
      this.users.appointmentsModal.show = false;
      this.users.appointmentsModal.item = defaultUser();
    },

    getGPSurgeryData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.gp_surgeries.loading = true;

        await this.api
          .get<PaginatedResponse<GPSurgery> | GPSurgery[]>(
            'admin/gpsurgeries/',
            {
              params: { ...payload },
            }
          )
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.gp_surgeries.data = response.data;
            } else {
              this.gp_surgeries.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.gp_surgeries.loading = false;
      });
    },

    getGPSurgeryItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.gp_surgeries.modal.loading = true;

        await this.api
          .get<GPSurgery>(url)
          .then((response: AxiosResponse) => {
            this.gp_surgeries.modal.item = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.gp_surgeries.modal.loading = false;
      });
    },

    createGPSurgeryItem(payload: GPSurgery): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.gp_surgeries.modal.loading = true;

        await this.api
          .post<GPSurgery>('admin/gpsurgeries/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.gp_surgeries.modal.loading = false;
      });
    },

    updateGPSurgeryItem(payload: GPSurgery): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.gp_surgeries.modal.loading = true;

        await this.api
          .put<GPSurgery>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.gp_surgeries.data.results.findIndex(
                (item: GPSurgery) => item.url === response.data.url
              );
              if (index !== -1) {
                this.gp_surgeries.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.gp_surgeries.modal.loading = false;
      });
    },

    deleteGPSurgeryItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showGPSurgeryModal(url?: string | null) {
      this.gp_surgeries.loading = true;
      if (url) {
        await this.getGPSurgeryItem(url);
      }
      this.gp_surgeries.modal.show = true;
      this.gp_surgeries.loading = false;
    },

    hideGPSurgeryModal() {
      this.gp_surgeries.modal.show = false;
      this.gp_surgeries.modal.item = defaultGPSurgery();
    },
    
    async startNhsSync() {
      return await this.api.post('admin/gpsurgeries/sync-start/');
    },
    
    async checkSyncStatus(taskId: string) {
      return await this.api.get('admin/gpsurgeries/sync-status/', {
        params: { task_id: taskId }
      });
    },

    getStaffData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.staff.loading = true;

        await this.api
          .get<PaginatedResponse<Staff> | Staff[]>('admin/staff/', {
            params: { ...payload },
          })
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.staff.data = response.data;
            } else {
              this.staff.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.staff.loading = false;
      });
    },

    getStaffItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.staff.modal.loading = true;

        await this.api
          .get<Staff>(url)
          .then((response: AxiosResponse) => {
            this.staff.modal.item = response.data;
            this.staff.modal.item.selected_competencies =
              this.competencies.data.results.filter((item: Competency) =>
                response.data.competencies.includes(item.url)
              );

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.staff.modal.loading = false;
      });
    },

    createStaffItem(payload: Staff): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.staff.modal.loading = true;

        await this.api
          .post<Staff>('admin/staff/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.staff.modal.loading = false;
      });
    },

    updateStaffItem(payload: Staff): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.staff.modal.loading = true;

        await this.api
          .put<Staff>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.staff.data.results.findIndex(
                (item: Staff) => item.url === response.data.url
              );
              if (index !== -1) {
                this.staff.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.staff.modal.loading = false;
      });
    },

    deleteStaffItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showStaffModal(url?: string | null) {
      this.staff.loading = true;
      if (url) {
        await this.getStaffItem(url);
      }
      this.staff.modal.show = true;
      this.staff.loading = false;
    },

    hideStaffModal() {
      this.staff.modal.show = false;
      this.staff.modal.item = defaultStaff();
    },

    getCompetencyData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.competencies.loading = true;

        await this.api
          .get<PaginatedResponse<Competency>>('admin/competencies/', {
            params: { ...payload },
          })
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.competencies.data = response.data;
            } else {
              this.competencies.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.competencies.loading = false;
      });
    },

    getCompetencyItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.competencies.modal.loading = true;

        await this.api
          .get<Competency>(url)
          .then((response: AxiosResponse) => {
            this.competencies.modal.item = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.competencies.modal.loading = false;
      });
    },

    createCompetencyItem(payload: Competency): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.competencies.modal.loading = true;

        await this.api
          .post<Competency>('admin/competencies/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.competencies.modal.loading = false;
      });
    },

    updateCompetencyItem(payload: Competency): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.competencies.modal.loading = true;

        await this.api
          .put<Competency>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.competencies.data.results.findIndex(
                (item: Competency) => item.url === response.data.url
              );
              if (index !== -1) {
                this.competencies.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.competencies.modal.loading = false;
      });
    },

    deleteCompetencyItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showCompetencyModal(url?: string | null) {
      this.competencies.loading = true;
      if (url) {
        await this.getCompetencyItem(url);
      }
      this.competencies.modal.show = true;
      this.competencies.loading = false;
    },

    hideCompetencyModal() {
      this.competencies.modal.show = false;
      this.competencies.modal.item = defaultCompetency();
    },

    getStaffCompetencyData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.staff_competencies.loading = true;

        await this.api
          .get<PaginatedResponse<StaffCompetency>>('admin/staffcompetencies/', {
            params: { ...payload },
          })
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.staff_competencies.data = response.data;
            } else {
              this.staff_competencies.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.staff_competencies.loading = false;
      });
    },

    getStaffCompetencyItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.staff_competencies.modal.loading = true;

        await this.api
          .get<StaffCompetency>(url)
          .then((response: AxiosResponse) => {
            this.staff_competencies.modal.item = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.staff_competencies.modal.loading = false;
      });
    },

    createStaffCompetencyItem(
      payload: StaffCompetency
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.staff_competencies.modal.loading = true;

        await this.api
          .post<StaffCompetency>('admin/staffcompetencies/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.staff_competencies.modal.loading = false;
      });
    },

    updateStaffCompetencyItem(
      payload: StaffCompetency
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.staff_competencies.modal.loading = true;

        await this.api
          .put<StaffCompetency>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.staff_competencies.data.results.findIndex(
                (item: StaffCompetency) => item.url === response.data.url
              );
              if (index !== -1) {
                this.staff_competencies.data.results[index] = response.data;
              }

              const keysToCheck = [
                '30-days-notice',
                '5-days-notice',
                '1-day-notice',
              ];
              const stored = JSON.parse(
                localStorage.getItem('competencyNotices') || '{}'
              );

              keysToCheck.forEach((noticeType) => {
                const key = `${response.data.id}-${noticeType}`;
                if (stored[key]) {
                  delete stored[key];
                }
              });

              localStorage.setItem('competencyNotices', JSON.stringify(stored));
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.staff_competencies.modal.loading = false;
      });
    },

    deleteStaffCompetencyItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showStaffCompetencyModal(url?: string | null) {
      this.staff_competencies.loading = true;
      if (url) {
        await this.getStaffCompetencyItem(url);
      }
      this.staff_competencies.modal.show = true;
      this.staff_competencies.loading = false;
    },

    hideStaffCompetencyModal() {
      this.staff_competencies.modal.show = false;
      this.staff_competencies.modal.item = defaultStaffCompetency();
    },

    getExpiringCompetencies(staffId: number) {
      return new Promise<void>(async (resolve, reject) => {
        const stored = JSON.parse(
          localStorage.getItem('competencyNotices') || '{}'
        );

        await this.api
          .get('/admin/staffcompetencies/expiring/', {
            params: { staff_id: staffId },
          })
          .then((response: AxiosResponse) => {
            const unreadNotices = response.data.filter(
              (item: ExpiringCompetencyNotice) => {
                const key = `${item.id}-${item.notice_type}`;
                return !stored[key];
              }
            );

            this.staff_competencies.expiring_dialog.show =
              unreadNotices.length > 0;
            this.staff_competencies.expiring_dialog.data = unreadNotices;

            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    markAllExpiringCompetenciesAsRead() {
      const stored = JSON.parse(
        localStorage.getItem('competencyNotices') || '{}'
      );

      this.staff_competencies.expiring_dialog.data.forEach(
        (item: ExpiringCompetencyNotice) => {
          const key = `${item.id}-${item.notice_type}`;
          stored[key] = true;
        }
      );

      localStorage.setItem('competencyNotices', JSON.stringify(stored));
      this.staff_competencies.expiring_dialog.show = false;
    },

    getSiteData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.sites.loading = true;

        await this.api
          .get<PaginatedResponse<Site>>('admin/sites/', {
            params: { ...payload },
          })
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.sites.data = response.data;
            } else {
              this.sites.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.sites.loading = false;
      });
    },

    getSiteItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.sites.modal.loading = true;

        await this.api
          .get<Site>(url)
          .then((response: AxiosResponse) => {
            this.sites.modal.item = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.sites.modal.loading = false;
      });
    },

    createSiteItem(payload: Site): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.sites.modal.loading = true;

        await this.api
          .post<Site>('admin/sites/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.sites.modal.loading = false;
      });
    },

    updateSiteItem(payload: Site): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.sites.modal.loading = true;

        await this.api
          .put<Site>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.sites.data.results.findIndex(
                (item: Site) => item.url === response.data.url
              );
              if (index !== -1) {
                this.sites.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.sites.modal.loading = false;
      });
    },

    deleteSiteItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showSiteModal(url?: string | null) {
      this.sites.loading = true;
      if (url) {
        await this.getSiteItem(url);
      }
      this.sites.modal.show = true;
      this.sites.loading = false;
    },

    hideSiteModal() {
      this.sites.modal.show = false;
      this.sites.modal.item = defaultSite();
    },

    downloadSiteQrPoster(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>((resolve, reject) => {
        this.api
          .get(`${url}download-qr-poster/`, {
            responseType: 'blob',
          })
          .then((response) => {
            resolve(response);
          })
          .catch((e) => {
            reject(e);
          });
      });
    },

    regenerateSiteQrToken(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>((resolve, reject) => {
        this.api
          .post(`${url}regenerate-qr/`)
          .then((response) => {
            resolve(response);
          })
          .catch((e) => {
            reject(e);
          });
      });
    },

    getRoomData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.rooms.loading = true;

        await this.api
          .get<PaginatedResponse<Room>>('admin/rooms/', {
            params: { ...payload },
          })
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.rooms.data = response.data;
            } else {
              this.rooms.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.rooms.loading = false;
      });
    },

    getRoomItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.rooms.modal.loading = true;

        await this.api
          .get<Room>(url)
          .then((response: AxiosResponse) => {
            this.rooms.modal.item = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.rooms.modal.loading = false;
      });
    },

    createRoomItem(payload: Room): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.rooms.modal.loading = true;

        await this.api
          .post<Room>('admin/rooms/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.rooms.modal.loading = false;
      });
    },

    updateRoomItem(payload: Room): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.rooms.modal.loading = true;

        await this.api
          .put<Room>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.rooms.data.results.findIndex(
                (item: Room) => item.url === response.data.url
              );
              if (index !== -1) {
                this.rooms.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.rooms.modal.loading = false;
      });
    },

    deleteRoomItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showRoomModal(url?: string | null) {
      this.rooms.loading = true;
      if (url) {
        await this.getRoomItem(url);
      }
      this.rooms.modal.show = true;
      this.rooms.loading = false;
    },

    hideRoomModal() {
      this.rooms.modal.show = false;
      this.rooms.modal.item = defaultRoom();
    },

    getEFormData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.eforms.loading = true;

        await this.api
          .get<PaginatedResponse<EForm>>('admin/eforms/', {
            params: { ...payload },
          })
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.eforms.data = response.data;
            } else {
              this.eforms.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.eforms.loading = false;
      });
    },

    getEFormItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.eforms.modal.loading = true;

        await this.api
          .get<EForm>(url)
          .then((response: AxiosResponse) => {
            this.eforms.modal.item = response.data;

            if (typeof response.data.formDefinition === 'string') {
              this.eforms.modal.item.formDefinition = JSON.parse(
                response.data.formDefinition
              );
            }

            this.eforms.modal.item.formDefinition =
              this.eforms.modal.item.formDefinition.map((item: FormElement) => {
                if (!item.id) {
                  item.id = `${item.type}-${uuidv4()}`;
                }
                return item;
              });

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.eforms.modal.loading = false;
      });
    },

    getEFormFieldCatalogue(): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get<SourceFieldOption[]>('admin/eforms/fields/')
          .then((response: AxiosResponse) => {
            this.eforms.fields = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    createEFormItem(payload: EForm): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.eforms.modal.loading = true;

        await this.api
          .post<EForm>('admin/eforms/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.eforms.modal.loading = false;
      });
    },

    updateEFormItem(payload: EForm): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.eforms.modal.loading = true;

        await this.api
          .put<EForm>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.eforms.data.results.findIndex(
                (item: EForm) => item.url === response.data.url
              );
              if (index !== -1) {
                this.eforms.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.eforms.modal.loading = false;
      });
    },

    deleteEFormItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showEFormModal(url?: string | null) {
      this.eforms.loading = true;
      if (url) {
        await this.getEFormItem(url);
      }
      this.eforms.modal.show = true;
      this.eforms.loading = false;
    },

    hideEFormModal() {
      this.eforms.modal.show = false;
      this.eforms.modal.item = defaultEForm();
    },

    getAppointmentData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointments.loading = true;

        await this.api
          .get<PaginatedResponse<Appointment>>('admin/appointments/', {
            params: { ...payload },
          })
          .then((response: AxiosResponse) => {
            this.appointments.data = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointments.loading = false;
      });
    },

    getAppointmentItem(
      url: string,
      payload: object = {}
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointments.modal.loading = true;

        await this.api
          .get<Appointment>(url, { params: { ...payload } })
          .then((response: AxiosResponse) => {
            this.appointments.modal.item = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointments.modal.loading = false;
      });
    },

    createAppointmentItem(payload: Appointment): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .post<Appointment>('admin/appointments/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    updateAppointmentItem(
      payload: Record<string, unknown> & {
        url: LocationQueryValue | LocationQueryValue[];
      },
      params: Record<string, unknown>
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointments.modal.loading = true;

        await this.api
          .put<Appointment>(payload.url as string, payload, { params })
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.appointments.data.results.findIndex(
                (item: Appointment) => item.url === response.data.url
              );
              if (index !== -1) {
                this.appointments.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointments.modal.loading = false;
      });
    },

    deleteAppointmentItem(
      url: string,
      cancellationNotes: string
    ): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        this.appointments.deleteLoading = true;

        await this.api
          .delete(url, {
            data: { cancellation_notes: cancellationNotes },
          })
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          })
          .finally(() => {
            this.appointments.deleteLoading = false;
          });
      });
    },

    async showAppointmentModal(url?: string | null, payload: object = {}) {
      this.appointments.loading = true;
      if (url) {
        await this.getAppointmentItem(url, payload);
      }
      this.appointments.modal.show = true;
      this.appointments.loading = false;
    },

    hideAppointmentModal() {
      this.appointments.modal.show = false;
      this.appointments.modal.item = defaultAppointment();
    },

    getCancelledAppointmentData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.cancelledAppointments.loading = true;

        await this.api
          .get<PaginatedResponse<Appointment>>(
            'admin/cancelled-appointments/',
            {
              params: { ...payload },
            }
          )
          .then((response: AxiosResponse) => {
            this.cancelledAppointments.data = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.cancelledAppointments.loading = false;
      });
    },

    getPatientsData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get<User[]>('admin/users/', { params: { ...payload } })
          .then((response: AxiosResponse) => {
            this.patients = response.data;
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    getAppointmentTypeData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointment_types.loading = true;

        await this.api
          .get<PaginatedResponse<AppointmentType>>('admin/appointmenttypes/', {
            params: { ...payload },
          })
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.appointment_types.data = response.data;
            } else {
              this.appointment_types.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointment_types.loading = false;
      });
    },

    getAppointmentTypeItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointment_types.modal.loading = true;

        await this.api
          .get<AppointmentType>(url)
          .then((response: AxiosResponse) => {
            this.appointment_types.modal.item = response.data;
            this.appointment_types.modal.item.selected_competencies =
              this.competencies.data.results.filter((item: Competency) =>
                response.data.competencies.includes(item.url)
              );
            this.appointment_types.modal.item.selected_categories =
              this.appointment_categories.data.results.filter(
                (item: AppointmentCategory) =>
                  response.data.categories.includes(item.url)
              );
            this.appointment_types.modal.item.selected_patient_forms =
              response.data.patient_forms
                .map((url: string) =>
                  this.eforms.data.results.find((item: EForm) => item.url === url)
                )
                .filter((item?: EForm): item is EForm => !!item);
            this.appointment_types.modal.item.selected_clinician_forms =
              response.data.clinician_forms
                .map((url: string) =>
                  this.eforms.data.results.find((item: EForm) => item.url === url)
                )
                .filter((item?: EForm): item is EForm => !!item);
            this.appointment_types.modal.item.selected_outcomes =
              this.appointment_outcomes.data.results.filter(
                (item: AppointmentOutcome) =>
                  response.data.outcomes.includes(item.url)
              );

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointment_types.modal.loading = false;
      });
    },

    createAppointmentTypeItem(
      payload: AppointmentType
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointment_types.modal.loading = true;

        await this.api
          .post<AppointmentType>('admin/appointmenttypes/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointment_types.modal.loading = false;
      });
    },

    updateAppointmentTypeItem(
      payload: AppointmentType
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointment_types.modal.loading = true;

        await this.api
          .put<AppointmentType>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.appointment_types.data.results.findIndex(
                (item: AppointmentType) => item.url === response.data.url
              );
              if (index !== -1) {
                this.appointment_types.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointment_types.modal.loading = false;
      });
    },

    uploadAppointmentTypeImage(payload: FormData, url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .post<string>(`${url}upload-image/`, payload)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    deleteAppointmentTypeItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showAppointmentTypeModal(url?: string | null) {
      this.appointment_types.loading = true;
      if (url) {
        await this.getAppointmentTypeItem(url);
      }
      this.appointment_types.modal.show = true;
      this.appointment_types.loading = false;
    },

    hideAppointmentTypeModal() {
      this.appointment_types.modal.show = false;
      this.appointment_types.modal.item = defaultAppointmentType();
    },

    getAppointmentTypeTagData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointment_type_tags.loading = true;

        await this.api
          .get<PaginatedResponse<AppointmentTypeTag>>(
            'admin/appointmenttypetags/',
            {
              params: { ...payload },
            }
          )
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.appointment_type_tags.data = response.data;
            } else {
              this.appointment_type_tags.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointment_type_tags.loading = false;
      });
    },

    getAppointmentTypeTagItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointment_type_tags.modal.loading = true;

        await this.api
          .get<AppointmentTypeTag>(url)
          .then((response: AxiosResponse) => {
            this.appointment_type_tags.modal.item = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointment_type_tags.modal.loading = false;
      });
    },

    createAppointmentTypeTagItem(
      payload: AppointmentTypeTag
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointment_type_tags.modal.loading = true;

        await this.api
          .post<AppointmentTypeTag>('admin/appointmenttypetags/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointment_type_tags.modal.loading = false;
      });
    },

    updateAppointmentTypeTagItem(
      payload: AppointmentTypeTag
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointment_type_tags.modal.loading = true;

        await this.api
          .put<AppointmentTypeTag>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.appointment_type_tags.data.results.findIndex(
                (item: AppointmentTypeTag) => item.url === response.data.url
              );
              if (index !== -1) {
                this.appointment_type_tags.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointment_type_tags.modal.loading = false;
      });
    },

    deleteAppointmentTypeTagItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showAppointmentTypeTagModal(url?: string | null) {
      this.appointment_type_tags.loading = true;
      if (url) {
        await this.getAppointmentTypeTagItem(url);
      }
      this.appointment_type_tags.modal.show = true;
      this.appointment_type_tags.loading = false;
    },

    hideAppointmentTypeTagModal() {
      this.appointment_type_tags.modal.show = false;
      this.appointment_type_tags.modal.item = defaultAppointmentTypeTag();
    },

    getAppointmentCategoryData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointment_categories.loading = true;

        await this.api
          .get<PaginatedResponse<AppointmentCategory>>(
            'admin/appointmentcategories/',
            {
              params: { ...payload },
            }
          )
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.appointment_categories.data = response.data;
            } else {
              this.appointment_categories.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointment_categories.loading = false;
      });
    },

    getAppointmentCategoryItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointment_categories.modal.loading = true;

        await this.api
          .get<AppointmentCategory>(url)
          .then((response: AxiosResponse) => {
            this.appointment_categories.modal.item = response.data;
            this.appointment_categories.modal.item.selected_parent =
              this.appointment_categories.data.results.find(
                (item: AppointmentCategory) => response.data.parent === item.url
              );

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointment_categories.modal.loading = false;
      });
    },

    createAppointmentCategoryItem(
      payload: AppointmentCategory
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointment_categories.modal.loading = true;

        await this.api
          .post<AppointmentCategory>('admin/appointmentcategories/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointment_categories.modal.loading = false;
      });
    },

    updateAppointmentCategoryItem(
      payload: AppointmentCategory
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointment_categories.modal.loading = true;

        await this.api
          .put<AppointmentCategory>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.appointment_categories.data.results.findIndex(
                (item: AppointmentCategory) => item.url === response.data.url
              );
              if (index !== -1) {
                this.appointment_categories.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointment_categories.modal.loading = false;
      });
    },

    deleteAppointmentCategoryItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showAppointmentCategoryModal(url?: string | null) {
      this.appointment_categories.loading = true;
      if (url) {
        await this.getAppointmentCategoryItem(url);
      }
      this.appointment_categories.modal.show = true;
      this.appointment_categories.loading = false;
    },

    hideAppointmentCategoryModal() {
      this.appointment_categories.modal.show = false;
      this.appointment_categories.modal.item = defaultAppointmentCategory();
    },

    getAppointmentOutcomeData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointment_outcomes.loading = true;

        await this.api
          .get<PaginatedResponse<AppointmentOutcome>>(
            'admin/appointmentoutcomes/',
            {
              params: { ...payload },
            }
          )
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.appointment_outcomes.data = response.data;
            } else {
              this.appointment_outcomes.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointment_outcomes.loading = false;
      });
    },

    getAppointmentOutcomeItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointment_outcomes.modal.loading = true;

        await this.api
          .get<AppointmentOutcome>(url)
          .then((response: AxiosResponse) => {
            this.appointment_outcomes.modal.item = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointment_outcomes.modal.loading = false;
      });
    },

    createAppointmentOutcomeItem(
      payload: AppointmentOutcome
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointment_outcomes.modal.loading = true;

        await this.api
          .post<AppointmentOutcome>('admin/appointmentoutcomes/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointment_outcomes.modal.loading = false;
      });
    },

    updateAppointmentOutcomeItem(
      payload: AppointmentOutcome
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.appointment_outcomes.modal.loading = true;

        await this.api
          .put<AppointmentOutcome>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.appointment_outcomes.data.results.findIndex(
                (item: AppointmentOutcome) => item.url === response.data.url
              );
              if (index !== -1) {
                this.appointment_outcomes.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.appointment_outcomes.modal.loading = false;
      });
    },

    deleteAppointmentOutcomeItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showAppointmentOutcomeModal(url?: string | null) {
      this.appointment_outcomes.loading = true;
      if (url) {
        await this.getAppointmentOutcomeItem(url);
      }
      this.appointment_outcomes.modal.show = true;
      this.appointment_outcomes.loading = false;
    },

    hideAppointmentOutcomeModal() {
      this.appointment_outcomes.modal.show = false;
      this.appointment_outcomes.modal.item = defaultAppointmentOutcome();
    },

    getVaccineProductData(
      payload: object = {},
      type = 'clinic'
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.vaccine_products.loading = true;
        const url =
          type === 'clinic'
            ? 'admin/vaccineproducts/'
            : 'private/vaccineproducts/';

        await this.api
          .get<PaginatedResponse<VaccineProduct> | VaccineProduct[]>(url, {
            params: { ...payload },
          })
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.vaccine_products.data = response.data;
            } else {
              this.vaccine_products.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.vaccine_products.loading = false;
      });
    },

    getVaccineProductItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.vaccine_products.modal.loading = true;

        await this.api
          .get<VaccineProduct>(url)
          .then((response: AxiosResponse) => {
            this.vaccine_products.modal.item = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.vaccine_products.modal.loading = false;
      });
    },

    createVaccineProductItem(payload: VaccineProduct): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.vaccine_products.modal.loading = true;

        await this.api
          .post<VaccineProduct>('admin/vaccineproducts/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.vaccine_products.modal.loading = false;
      });
    },

    updateVaccineProductItem(payload: VaccineProduct): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.vaccine_products.modal.loading = true;

        await this.api
          .put<VaccineProduct>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response?.status !== 400) {
              const index = this.vaccine_products.data.results.findIndex(
                (item: VaccineProduct) => item.url === response.data.url
              );
              if (index !== -1) {
                this.vaccine_products.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.vaccine_products.modal.loading = false;
      });
    },

    deleteVaccineProductItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showVaccineProductModal(url?: string | null) {
      this.vaccine_products.loading = true;
      if (url) {
        await this.getVaccineProductItem(url);
      }
      this.vaccine_products.modal.show = true;
      this.vaccine_products.loading = false;
    },

    hideVaccineProductModal() {
      this.vaccine_products.modal.show = false;
      this.vaccine_products.modal.item = defaultVaccineProduct();
    },

    getVaccinationHistoryData(
      type: string,
      payload: object = {}
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.vaccination_histories.loading = true;
        const url =
          type === 'clinic'
            ? 'admin/vaccinationhistories/'
            : 'private/vaccinationhistories/';

        await this.api
          .get<PaginatedResponse<VaccinationHistory> | VaccinationHistory[]>(
            url,
            { params: { ...payload } }
          )
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.vaccination_histories.data = response.data;
            } else {
              this.vaccination_histories.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.vaccination_histories.loading = false;
      });
    },

    getPrintableVaccinationHistoryData(
      type: string,
      payload: object = {}
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        const url =
          type === 'clinic'
            ? 'admin/vaccinationhistories/'
            : 'private/vaccinationhistories/';

        await this.api
          .get<VaccinationHistory[]>(url, { params: { ...payload } })
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    getVaccinationHistoryItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.vaccination_histories.modal.loading = true;

        await this.api
          .get<VaccinationHistory>(url)
          .then((response: AxiosResponse) => {
            this.vaccination_histories.modal.item = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.vaccination_histories.modal.loading = false;
      });
    },

    getVaccinationHistoryFields(): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get<VaccinationHistory>('admin/vaccinationhistories/fields/')
          .then((response: AxiosResponse) => {
            this.vaccination_histories.fields = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    getVaccinationHistoryAdministrationChoices(): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get<VaccinationHistory>(
            'admin/vaccinationhistories/administration-choices/'
          )
          .then((response: AxiosResponse) => {
            this.vaccination_histories.administration_choices = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    createVaccinationHistoryItem(
      payload: VaccinationHistory
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.vaccination_histories.modal.loading = true;

        await this.api
          .post<VaccinationHistory>('admin/vaccinationhistories/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.vaccination_histories.modal.loading = false;
      });
    },

    updateVaccinationHistoryItem(
      payload: VaccinationHistory
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.vaccination_histories.modal.loading = true;

        await this.api
          .put<VaccinationHistory>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response?.status !== 400) {
              const index = this.vaccination_histories.data.results.findIndex(
                (item: VaccinationHistory) => item.url === response.data.url
              );
              if (index !== -1) {
                this.vaccination_histories.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.vaccination_histories.modal.loading = false;
      });
    },

    deleteVaccinationHistoryItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showVaccinationHistoryModal(
      url?: string | null,
      extraData: VaccinationHistoryExtraData | Record<string, never> = {}
    ) {
      this.vaccination_histories.loading = true;
      if (url) {
        await this.getVaccinationHistoryItem(url);
      }
      this.vaccination_histories.extraData = extraData;
      this.vaccination_histories.modal.show = true;
      this.vaccination_histories.loading = false;
    },

    hideVaccinationHistoryModal() {
      this.vaccination_histories.modal.show = false;
      this.vaccination_histories.extraData = {};
      this.vaccination_histories.modal.item = defaultVaccinationHistory();
    },

    getVaccinationHistoryItemsByAppointment(
      payload = {}
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.vaccination_histories.appointmentModal.loading = true;

        await this.api
          .get<VaccinationHistory[]>(
            'admin/vaccinationhistories/get-vaccination-history-by-appointment/',
            { params: { ...payload } }
          )
          .then((response: AxiosResponse) => {
            this.vaccination_histories.appointmentModal.items = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.vaccination_histories.appointmentModal.loading = false;
      });
    },

    bulkUpdateVaccinationHistory(
      payload: VaccinationHistory[]
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.vaccination_histories.appointmentModal.loading = true;

        await this.api
          .put<VaccinationHistory[]>(
            'admin/vaccinationhistories/bulk-update/',
            payload
          )
          .then((response: AxiosResponse) => {
            this.vaccination_histories.appointmentModal.items = response.data;
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.vaccination_histories.appointmentModal.loading = false;
      });
    },

    async showAppointmentVaccinationModal(params = {}, extraData = {}) {
      await this.getVaccinationHistoryItemsByAppointment(params);
      this.vaccination_histories.appointmentModal.extraData =
        extraData as VaccinationHistoryExtraData;
      this.vaccination_histories.appointmentModal.show = true;
    },

    hideAppointmentVaccinationModal() {
      this.vaccination_histories.appointmentModal.show = false;
      this.vaccination_histories.appointmentModal.extraData = {};
      this.vaccination_histories.appointmentModal.item =
        defaultVaccinationHistory();
    },

    getAdviceCategoryData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.advice_categories.loading = true;

        await this.api
          .get<PaginatedResponse<AdviceCategory> | AdviceCategory[]>(
            'admin/advicecategories/',
            {
              params: { ...payload },
            }
          )
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.advice_categories.data = response.data;
            } else {
              this.advice_categories.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.advice_categories.loading = false;
      });
    },

    getAdviceCategoryItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.advice_categories.modal.loading = true;

        await this.api
          .get<AdviceCategory>(url)
          .then((response: AxiosResponse) => {
            this.advice_categories.modal.item = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.advice_categories.modal.loading = false;
      });
    },

    createAdviceCategoryItem(payload: AdviceCategory): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.advice_categories.modal.loading = true;

        await this.api
          .post<AdviceCategory>('admin/advicecategories/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.advice_categories.modal.loading = false;
      });
    },

    updateAdviceCategoryItem(payload: AdviceCategory): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.advice_categories.modal.loading = true;

        await this.api
          .put<AdviceCategory>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.advice_categories.data.results.findIndex(
                (item: AdviceCategory) => item.url === response.data.url
              );
              if (index !== -1) {
                this.advice_categories.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.advice_categories.modal.loading = false;
      });
    },

    deleteAdviceCategoryItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showAdviceCategoryModal(url?: string | null) {
      this.advice_categories.loading = true;
      if (url) {
        await this.getAdviceCategoryItem(url);
      }
      this.advice_categories.modal.show = true;
      this.advice_categories.loading = false;
    },

    hideAdviceCategoryModal() {
      this.advice_categories.modal.show = false;
      this.advice_categories.modal.item = defaultAdviceCategory();
    },

    getFieldIdentifierData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.field_identifiers.loading = true;

        await this.api
          .get<PaginatedResponse<FieldIdentifier> | FieldIdentifier[]>(
            'admin/fieldidentifiers/',
            {
              params: { ...payload },
            }
          )
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.field_identifiers.data = response.data;
            } else {
              this.field_identifiers.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.field_identifiers.loading = false;
      });
    },

    getFieldIdentifierItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.field_identifiers.modal.loading = true;

        await this.api
          .get<FieldIdentifier>(url)
          .then((response: AxiosResponse) => {
            this.field_identifiers.modal.item = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.field_identifiers.modal.loading = false;
      });
    },

    createFieldIdentifierItem(payload: FieldIdentifier): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.field_identifiers.modal.loading = true;

        await this.api
          .post<FieldIdentifier>('admin/fieldidentifiers/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.field_identifiers.modal.loading = false;
      });
    },

    updateFieldIdentifierItem(payload: FieldIdentifier): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.field_identifiers.modal.loading = true;

        await this.api
          .put<FieldIdentifier>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.field_identifiers.data.results.findIndex(
                (item: FieldIdentifier) => item.url === response.data.url
              );
              if (index !== -1) {
                this.field_identifiers.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.field_identifiers.modal.loading = false;
      });
    },

    deleteFieldIdentifierItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showFieldIdentifierModal(url?: string | null) {
      this.field_identifiers.loading = true;
      if (url) {
        await this.getFieldIdentifierItem(url);
      }
      this.field_identifiers.modal.show = true;
      this.field_identifiers.loading = false;
    },

    hideFieldIdentifierModal() {
      this.field_identifiers.modal.show = false;
      this.field_identifiers.modal.item = defaultFieldIdentifier();
    },

    getFieldIdentifierChoices(): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        if (this.field_identifiers.choices.source.length) {
          resolve();
          return;
        }

        await this.api
          .get('admin/fieldidentifiers/choices/')
          .then((response: AxiosResponse) => {
            this.field_identifiers.choices = response.data;

            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    getAllergyData(
      payload: object = {},
      type = 'clinic'
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.allergies.loading = true;
        const url =
          type === 'guest'
            ? 'public/allergies/'
            : type === 'clinic'
            ? 'admin/allergies/'
            : 'private/allergies/';

        await this.api
          .get<PaginatedResponse<Allergy> | Allergy[]>(url, {
            params: { ...payload },
          })
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.allergies.data = response.data;
            } else {
              this.allergies.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.allergies.loading = false;
      });
    },

    getAllergyItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.allergies.modal.loading = true;

        await this.api
          .get<Allergy>(url)
          .then((response: AxiosResponse) => {
            this.allergies.modal.item = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.allergies.modal.loading = false;
      });
    },

    createAllergyItem(payload: Allergy): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.allergies.modal.loading = true;

        await this.api
          .post<Allergy>('admin/allergies/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.allergies.modal.loading = false;
      });
    },

    updateAllergyItem(payload: Allergy): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.allergies.modal.loading = true;

        await this.api
          .put<Allergy>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.allergies.data.results.findIndex(
                (item: Allergy) => item.url === response.data.url
              );
              if (index !== -1) {
                this.allergies.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.allergies.modal.loading = false;
      });
    },

    deleteAllergyItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showAllergyModal(url?: string | null) {
      this.allergies.loading = true;
      if (url) {
        await this.getAllergyItem(url);
      }
      this.allergies.modal.show = true;
      this.allergies.loading = false;
    },

    hideAllergyModal() {
      this.allergies.modal.show = false;
      this.allergies.modal.item = defaultAllergy();
    },

    checkDuplicateUser(payload: {
      first_name: string;
      last_name: string;
      dob: string;
    }): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .post('admin/check-duplicate-user/', payload)
          .then((response: AxiosResponse) => {
            if (response.data.is_duplicate) {
              this.showDuplicatePatientsModal(response.data.duplicates);
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    showDuplicatePatientsModal(duplicates: User[]) {
      this.duplicatePatients.modal.show = true;
      this.duplicatePatients.modal.data = duplicates;
    },

    hideDuplicatePatientsModal() {
      this.duplicatePatients.modal.show = false;
      this.duplicatePatients.modal.data = [];
    },

    getPotentialDuplicateUsers(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.duplicatePatients.duplicateGroups.loading = true;

        await this.api
          .get('admin/potential-duplicate-users/', {
            params: { ...payload },
          })
          .then((response: AxiosResponse) => {
            this.duplicatePatients.duplicateGroups.data = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.duplicatePatients.duplicateGroups.loading = false;
      });
    },

    showPotentialDuplicateUsersModal(duplicate: DuplicateGroup) {
      this.duplicatePatients.duplicateGroups.modal.show = true;
      this.duplicatePatients.duplicateGroups.modal.item = duplicate;
    },

    hidePotentialDuplicateUsersModal() {
      this.duplicatePatients.duplicateGroups.modal.show = false;
      this.duplicatePatients.duplicateGroups.modal.item = {} as DuplicateGroup;
    },

    mergeDuplicateUsers(payload: {
      destination_id: number | null;
      source_ids: number[];
      overrides: Record<string, unknown>;
    }): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.duplicatePatients.duplicateGroups.loading = true;

        await this.api
          .post('admin/potential-duplicate-users/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.duplicatePatients.duplicateGroups.loading = false;
      });
    },

    saveVisibleColumns(columns: string[], tableName: string): void {
      localStorage.setItem(
        `${tableName}-visible-columns`,
        JSON.stringify(columns)
      );
    },

    loadVisibleColumns(tableName: string): string[] {
      const storedColumns = localStorage.getItem(
        `${tableName}-visible-columns`
      );
      return storedColumns ? JSON.parse(storedColumns) : [];
    },

    getEquipmentData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.equipment.loading = true;

        await this.api
          .get<PaginatedResponse<Equipment>>('admin/equipment/', {
            params: { ...payload },
          })
          .then((response: AxiosResponse) => {
            if (response.data.results) {
              this.equipment.data = response.data;
            } else {
              this.equipment.data.results = response.data;
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.equipment.loading = false;
      });
    },

    getEquipmentItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.equipment.modal.loading = true;

        await this.api
          .get<Equipment>(url)
          .then((response: AxiosResponse) => {
            this.equipment.modal.item = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.equipment.modal.loading = false;
      });
    },

    createEquipmentItem(payload: Equipment): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.equipment.modal.loading = true;

        await this.api
          .post<Equipment>('admin/equipment/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.equipment.modal.loading = false;
      });
    },

    updateEquipmentItem(payload: Equipment): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.equipment.modal.loading = true;

        await this.api
          .put<Equipment>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.equipment.data.results.findIndex(
                (item: Equipment) => item.url === response.data.url
              );
              if (index !== -1) {
                this.equipment.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.equipment.modal.loading = false;
      });
    },

    deleteEquipmentItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showEquipmentModal(url?: string | null) {
      this.equipment.loading = true;
      if (url) {
        await this.getEquipmentItem(url);
      }
      this.equipment.modal.show = true;
      this.equipment.loading = false;
    },

    hideEquipmentModal() {
      this.equipment.modal.show = false;
      this.equipment.modal.item = defaultEquipment();
    },

    getIncidentReportData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.incident_reports.loading = true;

        await this.api
          .get<PaginatedResponse<IncidentReport>>('admin/incidentreports/', {
            params: { ...payload },
          })
          .then((response: AxiosResponse) => {
            this.incident_reports.data = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.incident_reports.loading = false;
      });
    },

    getIncidentReportItem(url: string, isView = false): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        isView
          ? (this.incident_reports.viewModal.loading = true)
          : (this.incident_reports.modal.loading = true);

        await this.api
          .get<IncidentReport>(url)
          .then((response: AxiosResponse) => {
            isView
              ? (this.incident_reports.viewModal.item = response.data)
              : (this.incident_reports.modal.item = response.data);

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        isView
          ? (this.incident_reports.viewModal.loading = false)
          : (this.incident_reports.modal.loading = false);
      });
    },

    createIncidentReportItem(
      payload: IncidentReport | FormData
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.incident_reports.modal.loading = true;

        const config =
          payload instanceof FormData
            ? { headers: { 'Content-Type': 'multipart/form-data' } }
            : undefined;

        await this.api
          .post<IncidentReport>('admin/incidentreports/', payload, config)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.incident_reports.modal.loading = false;
      });
    },

    updateIncidentReportItem(
      payload: IncidentReport | FormData,
      url?: string
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.incident_reports.modal.loading = true;

        const targetUrl =
          url || (payload instanceof FormData ? '' : (payload as IncidentReport).url);

        const config =
          payload instanceof FormData
            ? { headers: { 'Content-Type': 'multipart/form-data' } }
            : undefined;

        await this.api
          .put<IncidentReport>(targetUrl, payload, config)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.incident_reports.data.results.findIndex(
                (item: IncidentReport) => item.url === response.data.url
              );
              if (index !== -1) {
                this.incident_reports.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.incident_reports.modal.loading = false;
      });
    },

    deleteIncidentReportItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showIncidentReportModal(url?: string | null) {
      this.incident_reports.loading = true;
      if (url) {
        await this.getIncidentReportItem(url);
      }
      this.incident_reports.modal.show = true;
      this.incident_reports.loading = false;
    },

    hideIncidentReportModal() {
      this.incident_reports.modal.show = false;
      this.incident_reports.modal.item = defaultIncidentReport();
    },

    async showIncidentReportViewModal(url?: string | null) {
      this.incident_reports.loading = true;
      if (url) {
        await this.getIncidentReportItem(url, true);
      }
      this.incident_reports.viewModal.show = true;
      this.incident_reports.loading = false;
    },

    async prepareReportForAction(url: string) {
      this.incident_reports.loading = true;
      await this.getIncidentReportItem(url, true);
      this.incident_reports.loading = false;
    },

    hideIncidentReportViewModal() {
      this.incident_reports.viewModal.show = false;
      this.incident_reports.viewModal.item = {} as IncidentReport;
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sendReportViaEmail(formData: any): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .post<IncidentReport>(
            `admin/incidentreports/${formData.reporter_id}/send-report-email/`,
            formData
          )
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    getITIncidentData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.it_incidents.loading = true;
        await this.api
          .get<PaginatedResponse<ITIncident>>('admin/itincidents/', {
            params: { ...payload },
          })
          .then((response: AxiosResponse) => {
            this.it_incidents.data = response.data;
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
        this.it_incidents.loading = false;
      });
    },

    getITIncidentItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.it_incidents.modal.loading = true;
        await this.api
          .get<ITIncident>(url)
          .then((response: AxiosResponse) => {
            this.it_incidents.modal.item = response.data;
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
        this.it_incidents.modal.loading = false;
      });
    },

    createITIncidentItem(payload: ITIncident): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.it_incidents.modal.loading = true;
        await this.api
          .post<ITIncident>('admin/itincidents/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
        this.it_incidents.modal.loading = false;
      });
    },

    updateITIncidentItem(payload: ITIncident): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.it_incidents.modal.loading = true;
        await this.api
          .put<ITIncident>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.it_incidents.data.results.findIndex(
                (item: ITIncident) => item.url === response.data.url
              );
              if (index !== -1) {
                this.it_incidents.data.results[index] = response.data;
              }
            }
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
        this.it_incidents.modal.loading = false;
      });
    },

    deleteITIncidentItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => resolve())
          .catch((e: AxiosError<string>) => reject(e));
      });
    },

    async showITIncidentModal(url?: string | null) {
      this.it_incidents.loading = true;
      if (url) {
        await this.getITIncidentItem(url);
      }
      this.it_incidents.modal.show = true;
      this.it_incidents.loading = false;
    },

    hideITIncidentModal() {
      this.it_incidents.modal.show = false;
      this.it_incidents.modal.item = defaultITIncident();
    },

    async showITIncidentViewModal(url?: string | null) {
      this.it_incidents.loading = true;
      if (url) {
        this.it_incidents.viewModal.loading = true;
        await this.api
          .get<ITIncident>(url)
          .then((response: AxiosResponse) => {
            this.it_incidents.viewModal.item = response.data;
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.it_incidents.viewModal.loading = false;
          });
      }
      this.it_incidents.viewModal.show = true;
      this.it_incidents.loading = false;
    },

    async prepareITIncidentForAction(url: string) {
      this.it_incidents.loading = true;
      await this.api
        .get<ITIncident>(url)
        .then((response: AxiosResponse) => {
          this.it_incidents.viewModal.item = response.data;
        })
        .catch((e) => {
          console.error(e);
        });
      this.it_incidents.loading = false;
    },

    hideITIncidentViewModal() {
      this.it_incidents.viewModal.show = false;
      this.it_incidents.viewModal.item = {} as ITIncident;
    },

    sendITIncidentViaEmail(formData: FormData): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .post<ITIncident>(
            `admin/itincidents/${formData.get('report_id')}/send-report-email/`,
            formData
          )
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    getCustomerComplaintData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.customer_complaints.loading = true;
        await this.api
          .get<PaginatedResponse<CustomerComplaint>>(
            'admin/customercomplaints/',
            { params: { ...payload } }
          )
          .then((response: AxiosResponse) => {
            this.customer_complaints.data = response.data;
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
        this.customer_complaints.loading = false;
      });
    },

    getCustomerComplaintItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.customer_complaints.modal.loading = true;
        await this.api
          .get<CustomerComplaint>(url)
          .then((response: AxiosResponse) => {
            this.customer_complaints.modal.item = response.data;
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
        this.customer_complaints.modal.loading = false;
      });
    },

    createCustomerComplaintItem(
      payload: CustomerComplaint
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.customer_complaints.modal.loading = true;
        await this.api
          .post<CustomerComplaint>('admin/customercomplaints/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
        this.customer_complaints.modal.loading = false;
      });
    },

    updateCustomerComplaintItem(
      payload: CustomerComplaint
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.customer_complaints.modal.loading = true;
        await this.api
          .put<CustomerComplaint>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.customer_complaints.data.results.findIndex(
                (item: CustomerComplaint) => item.url === response.data.url
              );
              if (index !== -1) {
                this.customer_complaints.data.results[index] = response.data;
              }
            }
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
        this.customer_complaints.modal.loading = false;
      });
    },

    deleteCustomerComplaintItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => resolve())
          .catch((e: AxiosError<string>) => reject(e));
      });
    },

    async showCustomerComplaintModal(url?: string | null) {
      this.customer_complaints.loading = true;
      if (url) {
        await this.getCustomerComplaintItem(url);
      }
      this.customer_complaints.modal.show = true;
      this.customer_complaints.loading = false;
    },

    hideCustomerComplaintModal() {
      this.customer_complaints.modal.show = false;
      this.customer_complaints.modal.item = defaultCustomerComplaint();
    },

    async showCustomerComplaintViewModal(url?: string | null) {
      this.customer_complaints.loading = true;
      if (url) {
        this.customer_complaints.viewModal.loading = true;
        await this.api
          .get<CustomerComplaint>(url)
          .then((response: AxiosResponse) => {
            this.customer_complaints.viewModal.item = response.data;
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.customer_complaints.viewModal.loading = false;
          });
      }
      this.customer_complaints.viewModal.show = true;
      this.customer_complaints.loading = false;
    },

    async prepareCustomerComplaintForAction(url: string) {
      this.customer_complaints.loading = true;
      await this.api
        .get<CustomerComplaint>(url)
        .then((response: AxiosResponse) => {
          this.customer_complaints.viewModal.item = response.data;
        })
        .catch((e) => {
          console.error(e);
        });
      this.customer_complaints.loading = false;
    },

    hideCustomerComplaintViewModal() {
      this.customer_complaints.viewModal.show = false;
      this.customer_complaints.viewModal.item = {} as CustomerComplaint;
    },

    sendCustomerComplaintViaEmail(formData: FormData): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .post<CustomerComplaint>(
            `admin/customercomplaints/${formData.get(
              'report_id'
            )}/send-report-email/`,
            formData
          )
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    getStaffSettingsData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.staff_settings.loading = true;

        await this.api
          .get<PaginatedResponse<StaffSettings>>('admin/staffsettings/', {
            params: { ...payload },
          })
          .then((response: AxiosResponse) => {
            this.staff_settings.data = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.staff_settings.loading = false;
      });
    },

    getStaffSettingsItem(url: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.staff_settings.modal.loading = true;

        await this.api
          .get<StaffSettings>(url)
          .then((response: AxiosResponse) => {
            this.staff_settings.modal.item = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.staff_settings.modal.loading = false;
      });
    },

    createStaffSettingsItem(payload: StaffSettings): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.staff_settings.modal.loading = true;

        await this.api
          .post<StaffSettings>('admin/staffsettings/', payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.staff_settings.modal.loading = false;
      });
    },

    updateStaffSettingsItem(payload: StaffSettings): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.staff_settings.modal.loading = true;

        await this.api
          .put<StaffSettings>(payload.url, payload)
          .then((response: AxiosResponse) => {
            if (response.status !== 400) {
              const index = this.staff_settings.data.results.findIndex(
                (item: StaffSettings) => item.url === response.data.url
              );
              if (index !== -1) {
                this.staff_settings.data.results[index] = response.data;
              }
            }

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.staff_settings.modal.loading = false;
      });
    },

    partiallyUpdateStaffSettings(
      url: string,
      payload: Partial<StaffSettings>
    ): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.staff_settings.loading = true;

        await this.api
          .patch<StaffSettings>(url, payload)
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.staff_settings.loading = false;
      });
    },

    deleteStaffSettingsItem(url: string): Promise<void> {
      return new Promise<void>(async (resolve, reject) => {
        await this.api
          .delete(url)
          .then(() => {
            resolve();
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    async showStaffSettingsModal(url?: string | null) {
      this.staff_settings.loading = true;
      if (url) {
        await this.getStaffSettingsItem(url);
      }
      this.staff_settings.modal.show = true;
      this.staff_settings.loading = false;
    },

    hideStaffSettingsModal() {
      this.staff_settings.modal.show = false;
      this.staff_settings.modal.item = defaultStaffSettings();
    },

    getSafetyMonthlyReportData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.monthly_reports.loading = true;

        await this.api
          .get<PaginatedResponse<SafetyReport>>('admin/reports/', {
            params: { ...payload, report_type: 'monthly' },
          })
          .then((response: AxiosResponse) => {
            this.monthly_reports.data = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.monthly_reports.loading = false;
      });
    },

    getSafetyAnnualReportData(payload: object = {}): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        this.monthly_reports.loading = true;

        await this.api
          .get<PaginatedResponse<SafetyReport>>('admin/reports/', {
            params: { ...payload, report_type: 'annual' },
          })
          .then((response: AxiosResponse) => {
            this.annual_reports.data = response.data;

            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });

        this.monthly_reports.loading = false;
      });
    },

    downloadSafetyReportPdf(reportId: number): Promise<AxiosResponse> {
      return new Promise<AxiosResponse>(async (resolve, reject) => {
        await this.api
          .get(`admin/reports/${reportId}/pdf/`, { responseType: 'blob' })
          .then((response: AxiosResponse) => {
            resolve(response);
          })
          .catch((e: AxiosError<string>) => {
            reject(e);
          });
      });
    },

    fetchSafetyReportByDate(payload: {
      report_type: unknown;
      year: number;
      month?: number;
    }): Promise<AxiosResponse<SafetyReport>> {
      return new Promise<AxiosResponse<SafetyReport>>((resolve, reject) => {
        this.api
          .get<SafetyReport>('admin/reports/get-by-date/', {
            params: payload,
          })
          .then((response) => {
            resolve(response);
          })
          .catch((e) => {
            reject(e);
          });
      });
    },

    updateSafetyReport(
      id: number,
      data: Partial<SafetyReport>
    ): Promise<AxiosResponse<SafetyReport>> {
      return new Promise<AxiosResponse<SafetyReport>>((resolve, reject) => {
        this.api
          .patch<SafetyReport>(`admin/reports/${id}/`, data)
          .then((response) => {
            resolve(response);
          })
          .catch((e) => {
            reject(e);
          });
      });
    },

    actionSafetyReport(id: number): Promise<AxiosResponse<SafetyReport>> {
      return new Promise<AxiosResponse<SafetyReport>>((resolve, reject) => {
        this.api
          .post<SafetyReport>(`admin/reports/${id}/action/`)
          .then((response) => {
            resolve(response);
          })
          .catch((e) => {
            reject(e);
          });
      });
    },

    getAnnualSummaryData(year: string): Promise<AxiosResponse> {
      return new Promise<AxiosResponse<SafetyReport>>((resolve, reject) => {
        this.api
          .get('admin/reports/annual-summary/', { params: { year } })
          .then((response) => {
            resolve(response);
          })
          .catch((e) => {
            reject(e);
          });
      });
    },
  },
});
